import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';

const BRAND = '#0aa375';
const BRAND_LIGHT = '#e6f7f2';
const BRAND_DARK = '#088a62';

export default function NotesModal({ ticket, domain, onClose }) {
  const [notes,    setNotes]    = useState([]);
  const [newNote,  setNewNote]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [posting,  setPosting]  = useState(false);

  useEffect(() => {
    const fetchNotes = async () => {
      setLoading(true);
      try {
        const res = await apiClient.post(`/api/tickets/notes`, { domain, ticketId: ticket.bgTicketId });
        setNotes(res.data || []);
      } catch {
        toast.error('Failed to load notes');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [ticket, domain]);

  const handlePostNote = async () => {
    if (!newNote.trim()) { toast.error('Note cannot be empty'); return; }
    setPosting(true);
    try {
      await apiClient.post(`/api/tickets/notes/add`, { domain, ticketId: ticket.bgTicketId, note: newNote.trim() });
      toast.success('Note posted');
      setNewNote('');
      const res = await apiClient.post(`/api/tickets/notes`, { domain, ticketId: ticket.bgTicketId });
      setNotes(res.data || []);
    } catch {
      toast.error('Failed to post note');
    } finally {
      setPosting(false);
    }
  };

  return (
    <>
      <style>{`
        .nm-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:1050;display:flex;align-items:center;justify-content:center; }
        .nm-modal { background:#fff;border-radius:8px;width:min(500px,96vw);max-height:90vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.18); }
        .nm-header { background:${BRAND};padding:8px 14px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
        .nm-header h6 { margin:0;color:#fff;font-size:13px;font-weight:700;letter-spacing:0.2px; }
        .nm-header .badge { font-size:11px;color:rgba(255,255,255,0.85);background:rgba(255,255,255,0.18);padding:2px 10px;border-radius:20px;margin-left:8px; }
        .nm-close { background:none;border:none;color:#fff;font-size:18px;line-height:1;cursor:pointer;padding:0 2px;opacity:0.85; }
        .nm-close:hover { opacity:1; }
        .nm-body { flex:1;overflow-y:auto;padding:12px 14px;display:flex;flex-direction:column;gap:10px; }
        .nm-section { border:1px solid #e9ecef;border-radius:6px;overflow:hidden; }
        .nm-section-title { font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:${BRAND};background:${BRAND_LIGHT};padding:5px 10px;border-bottom:1px solid #d4ede6; }
        .nm-section-body { padding:10px; }
        .nm-textarea { width:100%;font-size:12px;padding:6px 8px;border:1px solid #dee2e6;border-radius:4px;resize:vertical;min-height:80px;line-height:1.4;outline:none;font-family:inherit; }
        .nm-textarea:focus { border-color:${BRAND};box-shadow:0 0 0 2px ${BRAND_LIGHT}; }
        .nm-btn { height:28px;font-size:11px;font-weight:600;padding:0 16px;border-radius:4px;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:5px; }
        .nm-btn-primary { background:${BRAND};color:#fff; }
        .nm-btn-primary:hover:not(:disabled) { background:${BRAND_DARK}; }
        .nm-btn-primary:disabled { background:#6c757d;cursor:not-allowed; }
        .nm-btn-ghost { background:none;border:none;color:#6c757d;font-size:11px;padding:0 8px;cursor:pointer;height:28px;border-radius:4px; }
        .nm-btn-ghost:hover { background:#f8f9fa; }
        .nm-note-card { border:1px solid #e9ecef;border-radius:5px;overflow:hidden;margin-bottom:6px; }
        .nm-note-card:last-child { margin-bottom:0; }
        .nm-note-head { background:#f8f9fa;padding:5px 10px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #e9ecef; }
        .nm-note-author { font-size:11px;font-weight:700;color:#212529; }
        .nm-note-date { font-size:10px;color:#6c757d; }
        .nm-note-body { padding:7px 10px;font-size:12px;color:#212529;line-height:1.5;white-space:pre-wrap;word-break:break-word; }
        .nm-empty { text-align:center;padding:16px;font-size:12px;color:#adb5bd; }
        .nm-footer { padding:7px 14px;border-top:1px solid #e9ecef;display:flex;justify-content:flex-end;background:#fafafa;flex-shrink:0; }
        .nm-spinner { width:10px;height:10px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:nm-spin 0.7s linear infinite;display:inline-block; }
        .nm-spinner-dark { width:18px;height:18px;border:2px solid ${BRAND_LIGHT};border-top-color:${BRAND};border-radius:50%;animation:nm-spin 0.7s linear infinite;display:inline-block; }
        @keyframes nm-spin { to { transform:rotate(360deg); } }
      `}</style>

      <div className="nm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="nm-modal">

          {/* Header */}
          <div className="nm-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h6>Notes</h6>
              {ticket?.szTicketNumber && <span className="badge">{ticket.szTicketNumber}</span>}
            </div>
            <button className="nm-close" onClick={onClose}>×</button>
          </div>

          <div className="nm-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <div className="nm-spinner-dark" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontSize: '12px', color: '#6c757d' }}>Loading...</div>
              </div>
            ) : (
              <>
                {/* Add Note */}
                <div className="nm-section">
                  <div className="nm-section-title">Add Note</div>
                  <div className="nm-section-body">
                    <textarea className="nm-textarea" value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      placeholder="Type your note here..." />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '7px' }}>
                      <button className="nm-btn nm-btn-primary" onClick={handlePostNote} disabled={!newNote.trim() || posting}>
                        {posting ? <><span className="nm-spinner" />Posting...</> : 'Post Note'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Existing Notes */}
                <div className="nm-section">
                  <div className="nm-section-title">Existing Notes ({notes.length})</div>
                  <div className="nm-section-body" style={{ padding: notes.length === 0 ? '0' : '10px' }}>
                    {notes.length === 0 ? (
                      <div className="nm-empty">No notes yet.</div>
                    ) : (
                      notes.map((note, idx) => (
                        <div key={idx} className="nm-note-card">
                          <div className="nm-note-head">
                            <span className="nm-note-author">{note.szUserName || '—'}</span>
                            <span className="nm-note-date">{note.dtCreated || ''}</span>
                          </div>
                          <div className="nm-note-body">{note.szComment || '—'}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="nm-footer">
            <button className="nm-btn nm-btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
}